import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    AlternativeAddressDto,
    CarrierRulesMap,
    SavedShippingDataMap,
    ShippingTypeDto,
} from "@thkStore/Common/Types/Shipping/ShippingTypes";
import { Draft } from "immer";

export interface ShippingState {
    shippingTypeId?: string;
    shippingTypeOptions: ShippingTypeDto[];

    selectedCarrierId?: string;
    selectedCarrierName?: string;
    selectedShipViaId?: string;

    accountNumber: string;
    shippingInstructions: string;

    selectedAlternativeAddressId?: string;
    alternativeAddresses: AlternativeAddressDto[];

    savedShippingData: SavedShippingDataMap;
    carrierRules: CarrierRulesMap;

    showValidationErrors: boolean;
}

const initialState: ShippingState = {
    shippingTypeOptions: [],
    accountNumber: "",
    shippingInstructions: "",
    alternativeAddresses: [],
    savedShippingData: {},
    carrierRules: {},
    showValidationErrors: false,
};

const reducerShipping = createTypedReducerWithImmer(initialState, {
    "Shipping/SetShippingType": (
        draft: Draft<ShippingState>,
        action: { shippingTypeId: string },
    ) => {
        draft.shippingTypeId = action.shippingTypeId;
        draft.selectedCarrierId = undefined;
        draft.selectedCarrierName = undefined;
        draft.selectedShipViaId = undefined;
        draft.accountNumber = "";
        draft.shippingInstructions = "";
        draft.selectedAlternativeAddressId = undefined;
        draft.showValidationErrors = false;
    },
    "Shipping/SetSelectedCarrier": (
        draft: Draft<ShippingState>,
        action: { carrierId?: string; carrierName?: string; shipViaId?: string },
    ) => {
        const carrierChanged = (action.carrierId ?? "") !== (draft.selectedCarrierId ?? "");
        draft.selectedCarrierId = action.carrierId;
        draft.selectedCarrierName = action.carrierName;
        draft.selectedShipViaId = action.shipViaId;
        if (carrierChanged) {
            draft.accountNumber = "";
            draft.shippingInstructions = "";
            draft.showValidationErrors = false;
        }
    },
    "Shipping/SetShipVia": (
        draft: Draft<ShippingState>,
        action: { shipViaId?: string },
    ) => {
        draft.selectedShipViaId = action.shipViaId;
    },
    "Shipping/SetAccountNumber": (
        draft: Draft<ShippingState>,
        action: { accountNumber: string },
    ) => {
        draft.accountNumber = action.accountNumber;
    },
    "Shipping/SetShippingInstructions": (
        draft: Draft<ShippingState>,
        action: { shippingInstructions: string },
    ) => {
        draft.shippingInstructions = action.shippingInstructions;
    },
    "Shipping/SetAlternativeAddress": (
        draft: Draft<ShippingState>,
        action: { alternativeAddressId?: string },
    ) => {
        draft.selectedAlternativeAddressId = action.alternativeAddressId;
    },
    "Shipping/LoadSavedShippingData": (
        draft: Draft<ShippingState>,
        action: {
            shippingTypeOptions: ShippingTypeDto[];
            alternativeAddresses: AlternativeAddressDto[];
            savedShippingData: SavedShippingDataMap;
            carrierRules: CarrierRulesMap;
        },
    ) => {
        draft.shippingTypeOptions = action.shippingTypeOptions ?? [];
        draft.alternativeAddresses = action.alternativeAddresses ?? [];
        draft.savedShippingData = action.savedShippingData ?? {};
        draft.carrierRules = action.carrierRules ?? {};
    },
    "Shipping/AddAlternativeAddress": (
        draft: Draft<ShippingState>,
        action: { address: AlternativeAddressDto },
    ) => {
        if (!action.address?.id) return;
        if (draft.alternativeAddresses.some(a => a.id === action.address.id)) return;
        draft.alternativeAddresses.push(action.address);
    },
    "Shipping/SetShowValidationErrors": (
        draft: Draft<ShippingState>,
        action: { showValidationErrors: boolean },
    ) => {
        draft.showValidationErrors = action.showValidationErrors;
    },
});

(reducers as any).shipping = reducerShipping;

export type ApplicationShippingState = {
    shipping: ShippingState;
} & ApplicationState;

type Reducers = {
    shipping: typeof reducerShipping;
};

export type ShippingActions = Parameters<Reducers[keyof Reducers]>[1] | AnyAction;
